import {
  IChatBlock,
  IChatMessage,
  IConfirmRequest,
  IOdooConnection,
  ISSEEvent,
} from "@pages/askDost/types";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { REHYDRATE } from "redux-persist";

// ----------------------------------------------------------------------

export interface ISavedPromptEntry {
  id: string;
  text: string;
}

export interface IAskDostMemoryEntry {
  id: string;
  text: string;
  when: string;
}

type AskDostChatState = {
  messages: IChatMessage[];
  savedPrompts: ISavedPromptEntry[];
  memories: IAskDostMemoryEntry[];
  level: "auto" | "minimal" | "recommended" | "strong";
  focus: "documents" | "mappings" | "workflows" | "auto";
  thinking: boolean;
  isPanelOpen: boolean;
  activeConversationId: string | null;
  activeConversationTitle: string | null;
  odoo: IOdooConnection | null;
  odooSyncStatus: "idle" | "syncing";
};

const initialState: AskDostChatState = {
  messages: [],
  savedPrompts: [],
  memories: [],
  level: "recommended",
  focus: "auto",
  thinking: false,
  isPanelOpen: false,
  activeConversationId: null,
  activeConversationTitle: null,
  odoo: null,
  odooSyncStatus: "idle",
};

let blockIdCounter = 0;
const nextBlockId = () => `block-${++blockIdCounter}`;

const sanitizeMessages = (messages: IChatMessage[]): IChatMessage[] => {
  if (!messages || !Array.isArray(messages)) return [];

  return messages
    .map((msg) => {
      if (msg.blocks && Array.isArray(msg.blocks)) {
        return {
          ...msg,
          blocks: msg.blocks.filter(
            (block) =>
              block.type !== "thinking" &&
              block.toolStatus !== "in_progress" &&
              block.toolStatus !== "pending" &&
              !(
                block.type === "code" &&
                block.code?.is_error === undefined &&
                block.code?.stdout === undefined &&
                block.code?.stderr === undefined
              )
          ),
        };
      }
      return msg;
    })
    .filter((msg) => {
      if (msg.sender === "ai") {
        const hasBlocks = msg.blocks && msg.blocks.length > 0;
        const hasContent = msg.content && msg.content.trim().length > 0;
        const hasAttachments = msg.attachments && msg.attachments.length > 0;

        if (!hasBlocks && !hasContent && !hasAttachments) {
          return false;
        }
      }
      return true;
    });
};

const slice = createSlice({
  name: "askDostChat",
  initialState,
  reducers: {
    setLevel(
      state,
      action: PayloadAction<"auto" | "minimal" | "recommended" | "strong">,
    ) {
      state.level = action.payload;
    },
    setFocus(
      state,
      action: PayloadAction<"documents" | "mappings" | "workflows" | "auto">,
    ) {
      state.focus = action.payload;
    },
    setThinking(state, action: PayloadAction<boolean>) {
      state.thinking = action.payload;
    },
    openPanel(state) {
      state.isPanelOpen = true;
    },
    closePanel(state) {
      state.isPanelOpen = false;
    },
    togglePanel(state) {
      state.isPanelOpen = !state.isPanelOpen;
    },

    setActiveConversation(
      state,
      action: PayloadAction<{ id: string | null; title: string | null }>,
    ) {
      state.activeConversationId = action.payload.id;
      state.activeConversationTitle = action.payload.title;
    },

    setActiveConversationWithMessages(
      state,
      action: PayloadAction<{
        id: string | null;
        title: string | null;
        messages: IChatMessage[];
      }>,
    ) {
      state.activeConversationId = action.payload.id;
      state.activeConversationTitle = action.payload.title;
      state.messages = sanitizeMessages(action.payload.messages);
    },

    setMessages(state, action: PayloadAction<IChatMessage[]>) {
      state.messages = sanitizeMessages(action.payload);
    },

    addMessage(state, action: PayloadAction<IChatMessage>) {
      state.messages.push(action.payload);
    },

    clearMessages(state) {
      state.messages = [];
      state.activeConversationId = null;
      state.activeConversationTitle = null;
    },

    setSavedPrompts(state, action: PayloadAction<ISavedPromptEntry[]>) {
      state.savedPrompts = action.payload;
    },

    setOdooConnection(state, action: PayloadAction<IOdooConnection>) {
      state.odoo = action.payload;
    },

    setOdooSyncStatus(state, action: PayloadAction<"idle" | "syncing">) {
      state.odooSyncStatus = action.payload;
    },

    disconnectOdoo(state) {
      state.odoo = null;
      state.odooSyncStatus = "idle";
    },

    applyStreamEvent(state, action: PayloadAction<ISSEEvent>) {
      if (state.messages.length === 0) return;

      const event = action.payload;
      const lastMessage = state.messages[state.messages.length - 1];
      const blocks = lastMessage.blocks ?? (lastMessage.blocks = []);
      const lastBlock = blocks[blocks.length - 1];

      switch (event.type) {
        case "conversation": {
          state.activeConversationId = event.id || state.activeConversationId;
          state.activeConversationTitle =
            event.title || state.activeConversationTitle;
          break;
        }

        case "text": {
          if (lastBlock && lastBlock.type === "text") {
            lastBlock.content = (lastBlock.content || "") + (event.text || "");
          } else {
            blocks.push({
              id: nextBlockId(),
              type: "text",
              content: event.text || "",
            });
          }
          break;
        }

        case "thinking": {
          const lastThinkingBlockIndex = blocks
            .map((b) => b.type)
            .lastIndexOf("thinking");

          if (lastThinkingBlockIndex !== -1) {
            const lastThinkingBlock = blocks[lastThinkingBlockIndex];
            lastThinkingBlock.content =
              (lastThinkingBlock.content || "") + (event.text || "");
          } else {
            blocks.push({
              id: nextBlockId(),
              type: "thinking",
              content: event.text || "",
            });
          }
          break;
        }

        case "tool": {
          const lastThinkingBlockIndex =
            blocks.length > 0 && blocks[blocks.length - 1].type === "thinking"
              ? blocks.length - 1
              : -1;

          if (lastThinkingBlockIndex !== -1) {
            const thinkingBlock = blocks[lastThinkingBlockIndex];
            thinkingBlock.toolName = event.name;
            thinkingBlock.toolInput = event.input;
            thinkingBlock.toolStatus = "in_progress";
          } else {
            blocks.push({
              id: nextBlockId(),
              type: "tool",
              toolName: event.name,
              toolInput: event.input,
              toolStatus: "in_progress",
            });
          }
          break;
        }

        case "tool_progress": {
          const lastThinkingBlockIndex =
            blocks.length > 0 && blocks[blocks.length - 1].type === "thinking"
              ? blocks.length - 1
              : -1;

          if (lastThinkingBlockIndex !== -1) {
            blocks[lastThinkingBlockIndex].toolStatus = "in_progress";
          }
          break;
        }

        case "tool_done": {
          const lastThinkingBlockIndex =
            blocks.length > 0 && blocks[blocks.length - 1].type === "thinking"
              ? blocks.length - 1
              : -1;

          if (lastThinkingBlockIndex !== -1) {
            const thinkingBlock = blocks[lastThinkingBlockIndex];
            thinkingBlock.toolStatus = event.is_error ? "error" : "done";
            thinkingBlock.toolSummary = event.summary;
            thinkingBlock.toolIsError = event.is_error;
          }
          break;
        }

        case "table_pending": {
          blocks.push({
            id: nextBlockId(),
            type: "table",
            title: event.title,
            toolStatus: "pending",
          });
          break;
        }

        case "table_start": {
          const lastPendingTableIndex = blocks
            .map((b) => b.type === "table" && b.toolStatus === "pending")
            .lastIndexOf(true);

          if (lastPendingTableIndex !== -1) {
            blocks[lastPendingTableIndex].title = event.title;
            blocks[lastPendingTableIndex].columns = event.columns;
            blocks[lastPendingTableIndex].rows = [];
          }
          break;
        }

        case "table_rows": {
          const lastTableIndex = blocks
            .map((b) => b.type === "table")
            .lastIndexOf(true);

          if (lastTableIndex !== -1 && event.rows) {
            const tableBlock = blocks[lastTableIndex];
            tableBlock.rows = (tableBlock.rows || []).concat(event.rows);
          }
          break;
        }

        case "table": {
          const lastPendingTableIndex = blocks
            .map((b) => b.type === "table" && b.toolStatus === "pending")
            .lastIndexOf(true);

          if (lastPendingTableIndex !== -1) {
            const pendingBlock = blocks[lastPendingTableIndex];
            pendingBlock.title = event.title ?? pendingBlock.title;
            pendingBlock.columns = event.columns;
            pendingBlock.rows = event.rows;
            pendingBlock.toolStatus = "done";
          } else {
            blocks.push({
              id: nextBlockId(),
              type: "table",
              title: event.title,
              columns: event.columns,
              rows: event.rows,
              toolStatus: "done",
            });
          }
          break;
        }

        case "chart": {
          if (event.spec) {
            blocks.push({
              id: nextBlockId(),
              type: "chart",
              spec: event.spec,
            });
          }
          break;
        }

        case "file_created": {
          blocks.push({
            id: nextBlockId(),
            type: "file",
            fileId: event.file_id,
            fileName: event.name,
            fileSize: event.size,
            mediaType: event.media_type,
          });
          break;
        }

        case "code_start": {
          const isReopeningUnresolvedCode =
            lastBlock?.type === "code" &&
            lastBlock.code &&
            lastBlock.code.stdout === undefined &&
            lastBlock.code.stderr === undefined &&
            lastBlock.code.return_code === undefined;

          if (isReopeningUnresolvedCode) {
            lastBlock!.code = {
              summary: `Running ${event.tool || "code"}...`,
              meta: event.tool || "sandbox",
              lines: [],
            };
            break;
          }

          blocks.push({
            id: nextBlockId(),
            type: "code",
            code: {
              summary: `Running ${event.tool || "code"}...`,
              meta: event.tool || "sandbox",
              lines: [],
            },
          });
          break;
        }

        case "code": {
          const lastCodeBlockIndex = blocks
            .map((b) => b.type)
            .lastIndexOf("code");
          if (lastCodeBlockIndex !== -1 && blocks[lastCodeBlockIndex].code) {
            const codeBlock = blocks[lastCodeBlockIndex].code;
            if (codeBlock && event.text) {
              if (codeBlock.lines.length === 0) {
                codeBlock.lines.push(event.text);
              } else {
                const lastLineIndex = codeBlock.lines.length - 1;
                const newText = codeBlock.lines[lastLineIndex] + event.text;
                const newLines = newText.split("\n");
                codeBlock.lines.splice(lastLineIndex, 1, ...newLines);
              }
            }
          }
          break;
        }

        case "code_result": {
          const lastCodeBlockIndex = blocks
            .map((b) => b.type)
            .lastIndexOf("code");
          if (lastCodeBlockIndex !== -1 && blocks[lastCodeBlockIndex].code) {
            const codeBlock = blocks[lastCodeBlockIndex].code;
            if (codeBlock) {
              codeBlock.stdout = event.stdout;
              codeBlock.stderr = event.stderr;
              codeBlock.return_code = event.return_code;
              codeBlock.is_error = event.is_error;
              codeBlock.summary = event.is_error
                ? "Execution failed"
                : "Execution complete";
            }
          }
          break;
        }

        case "clarify_request" as any: {
          blocks.push({
            id: nextBlockId(),
            type: "mcq",
            mcq: {
              question: (event as any).question || "",
              hint: "",
              options: (event as any).options || [],
            },
          });
          break;
        }

        case "confirm_request": {
          const incomingRules = (event as any).rules;

          const isGroupableWithLast =
            incomingRules &&
            lastBlock?.type === "confirm_request" &&
            lastBlock.rules &&
            lastBlock.confirmTool === event.tool;

          if (isGroupableWithLast) {
            const existingEntries =
              lastBlock!.mappingRuleEntries ??
              (lastBlock!.write_id
                ? [{ write_id: lastBlock!.write_id, rules: lastBlock!.rules }]
                : []);
            lastBlock!.mappingRuleEntries = existingEntries.concat({
              write_id: event?.write_id!,
              rules: incomingRules,
            });
            break;
          }

          blocks.push({
            id: nextBlockId(),
            type: "confirm_request",
            confirmTool: event.tool,
            confirmInput: event.input,
            confirmSummary: event.summary,
            confirmDetails: event.details,
            write_id: event?.write_id,
            rules: incomingRules,
          });
          break;
        }

        case "spec_request": {
          blocks.push({
            id: nextBlockId(),
            type: "confirm_request",
            confirmTool: "dashboard_spec",
            confirmSummary: event.summary,
            write_id: event.spec_id,
          });
          break;
        }

        case "error": {
          blocks.push({
            id: nextBlockId(),
            type: "error",
            content: event.message,
          });
          break;
        }

        case "sources": {
          const lastTextBlockIndex = blocks
            .map((b) => b.type)
            .lastIndexOf("text");
          if (lastTextBlockIndex !== -1 && event.sources) {
            blocks[lastTextBlockIndex].referencedDocs = event.sources;
          }
          break;
        }

        case "session_expired": {
          blocks.push({
            id: nextBlockId(),
            type: "error",
            content: "Your session has expired. Please log in again.",
          });
          break;
        }

        default:
          break;
      }
    },

    setConfirmResolution(
      state,
      action: PayloadAction<{
        blockId: string;
        resolution: IConfirmRequest;
        writeId?: string;
      }>,
    ) {
      if (state.messages.length === 0) return;
      const { blockId, resolution, writeId } = action.payload;
      const lastMessage = state.messages[state.messages.length - 1];
      lastMessage.blocks = (lastMessage.blocks ?? []).map((block) =>
        block.id === blockId
          ? {
              ...block,
              confirmResolution: resolution,
              itemResolutions: writeId
                ? { ...block.itemResolutions, [writeId]: resolution }
                : block.itemResolutions,
            }
          : block,
      );
    },

    toggleSavedPrompt(state, action: PayloadAction<string>) {
      const text = action.payload;
      const existingIndex = state.savedPrompts.findIndex(
        (p) => p.text === text,
      );
      if (existingIndex !== -1) {
        state.savedPrompts.splice(existingIndex, 1);
      } else {
        state.savedPrompts.push({
          id: `saved-prompt-${Date.now()}-${Math.random()
            .toString(36)
            .slice(2, 6)}`,
          text,
        });
      }
    },
    removeSavedPrompt(state, action: PayloadAction<string>) {
      state.savedPrompts = state.savedPrompts.filter(
        (p) => p.id !== action.payload,
      );
    },
    setMemories(state, action: PayloadAction<IAskDostMemoryEntry[]>) {
      state.memories = action.payload;
    },

    removeMemory(state, action: PayloadAction<string>) {
      state.memories = state.memories.filter((m) => m.id !== action.payload);
    },
    clearMemories(state) {
      state.memories = [];
    },

    patchBlock(
      state,
      action: PayloadAction<{
        messageId: string;
        blockId: string;
        patch: Partial<IChatBlock>;
      }>,
    ) {
      const { messageId, blockId, patch } = action.payload;
      const message = state.messages.find((m) => m.id === messageId);
      const block = message?.blocks?.find((b) => b.id === blockId);
      if (block) Object.assign(block, patch);
    },

    appendBlocks(
      state,
      action: PayloadAction<{ messageId: string; blocks: IChatBlock[] }>,
    ) {
      const { messageId, blocks } = action.payload;
      const message = state.messages.find((m) => m.id === messageId);
      if (!message) return;
      message.blocks = (message.blocks ?? []).concat(blocks);
    },
  },
  extraReducers: (builder) => {
    builder.addCase(REHYDRATE, (state, action: any) => {
      if (!action.payload) return;
      const persistedMessages = action.payload.askDostChat?.messages || action.payload.messages;
      
      if (persistedMessages && Array.isArray(persistedMessages)) {
        state.messages = sanitizeMessages(persistedMessages);
      }
    });
  },
});

// Reducer
export default slice.reducer;

// Actions
export const {
  setMessages,
  addMessage,
  clearMessages,
  applyStreamEvent,
  setConfirmResolution,
  toggleSavedPrompt,
  removeSavedPrompt,
  removeMemory,
  clearMemories,
  setMemories,
  patchBlock,
  appendBlocks,
  setLevel,
  setFocus,
  setThinking,
  openPanel,
  closePanel,
  togglePanel,
  setActiveConversation,
  setActiveConversationWithMessages,
  setSavedPrompts,
  setOdooConnection,
  setOdooSyncStatus,
  disconnectOdoo,
} = slice.actions;
